import React, { useState, useEffect } from 'react';

const CompanyForm = ({ company = null, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    industry: '',
    username: '',
    password: '',
    location: ''
  });

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || company.mobile || '',
        industry: company.industry || '',
        username: company.username || '',
        password: company.password || '',
        location: company.location || company.address || ''
      });
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Validate email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate username (should be at least 3 characters)
    if (formData.username && formData.username.length < 3) {
      newErrors.username = 'Username should be at least 3 characters long';
    }

    // Check for spaces in username
    if (formData.username && formData.username.includes(' ')) {
      newErrors.username = 'Username cannot contain spaces';
    }

    // Validate password length
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password should be at least 6 characters long';
    }

    // Validate phone number format (simple validation)
    if (formData.phone && !/^[0-9\-\+\(\)\s]{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form before submission
    if (validateForm()) {
      console.log('Form data is valid, submitting:', formData);
      onSubmit(formData);
    } else {
      console.error('Form validation failed:', errors);
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
      }
    }
  };

  return (
    <div style={styles.formContainer}>
      <h2 style={styles.title}>
        {company ? 'Edit Company' : 'Add New Company'}
      </h2>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="name" style={styles.label}>Company Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={errors.email ? {...styles.input, ...styles.inputError} : styles.input}
              required
            />
            {errors.email && <div style={styles.errorText}>{errors.email}</div>}
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="phone" style={styles.label}>Phone</label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={errors.phone ? {...styles.input, ...styles.inputError} : styles.input}
              required
            />
            {errors.phone && <div style={styles.errorText}>{errors.phone}</div>}
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="industry" style={styles.label}>Industry</label>
            <input
              type="text"
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="username" style={styles.label}>Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              style={errors.username ? {...styles.input, ...styles.inputError} : styles.input}
              required
            />
            {errors.username && <div style={styles.errorText}>{errors.username}</div>}
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={errors.password ? {...styles.input, ...styles.inputError} : styles.input}
              required
            />
            {errors.password && <div style={styles.errorText}>{errors.password}</div>}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="location" style={styles.label}>Location</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.buttonGroup}>
          <button type="button" onClick={onCancel} style={styles.cancelButton}>
            Cancel
          </button>
          <button type="submit" style={styles.submitButton} id="companyFormSubmitButton">
            {company ? 'Update' : 'Add'} Company
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#2c3e50',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
  },
  formGroup: {
    flex: 1,
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  inputError: {
    borderColor: '#e74c3c',
    boxShadow: '0 0 0 1px #e74c3c',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
};

export default CompanyForm;
